/* cf-gate-exact-refine.js - Click Fencing, 2026-09-01 (later session)
 *
 * Pete: "perfmesh exact gate location isnt working" / "exact gate isnt working correctly"
 *       "on the exact gate measurement, it should take the rake into account if its there."
 *       "we cant cut a rake panel but we could snap the gate up to it if the rake is in the way?"
 *
 * ---------------------------------------------------------------------------------------
 * THE ROOT CAUSE, read out of cf-gate-controls.js itself and confirmed live.
 *
 * Their handler is exactApply(n) -> snapTo(n, target):
 *
 *     function snapTo(n,target){
 *       var offs = offsets(n)||[], ... for each k: pick the offs[k] nearest target -> best
 *       applyPos(n,best);
 *       var bestOff = drawnOffsetFor(n);
 *       if (bestOff === null) return { slot:best, offset:offs[best], measured:false };   // <-- HERE
 *       ... then try best-1 and best+1 by measurement ...
 *     }
 *
 * offsets(n) is built from calculation.panelWidths, which does NOT describe the drawn fence
 * once a rake or a cut panel exists. Measured live on PerfMesh 8725mm raked:
 *
 *     panelWidths (what the ladder is built from)   [3000, 3000, 270]
 *     ladder it produces                            [50, 3100, 6150, 6470]
 *     what is actually DRAWN                        50 | 1185 rake | 50 | 2500 cut |
 *                                                   50 | 970 gate | 50 | 3000 | 50 | 770 | 50
 *
 * The ladder is meant to be rescued by the measured ±1 pass - but on a raked section their
 * drawnOffsetFor() returns null, so snapTo takes the early return and reports offs[best],
 * a number off the wrong ladder. That is not a guess: asking for 1200mm from the right put
 * the note at "Gate placed 6470mm from the left" while the drawing plainly showed the gate
 * at 6885. 6470 is ladder slot 3 exactly. So on precisely the sections Pete reported, their
 * measurement is dead and the customer is shown a number that is not where the gate is.
 *
 * ---------------------------------------------------------------------------------------
 * WHAT THIS DOES. It takes the Apply click in the CAPTURE phase and, when it can read the
 * drawing, stops the event reaching their handler and does the whole job itself:
 *
 *   1. reads the requested distance and side straight off the real controls;
 *   2. walks the gate to the left end, then steps right while each move measurably gets
 *      CLOSER to what was asked - measuring off the drawing, which is rake-aware for free
 *      because the rake is in the drawing;
 *   3. steps back one when it passes the best spot;
 *   4. writes an honest note saying where the gate actually ended up and, if that is not
 *      what was asked for, why.
 *
 * TWO THINGS THAT LOOK LIKE DETAILS AND ARE NOT.
 *
 * The Move buttons DO NOT DISABLE at the ends of the run - measured: at slot 0 Move-left
 * still reports disabled=false and clicking it does nothing. So "did we run out of road" can
 * never be answered by b.disabled. An early draft trusted the flag, read the no-op as "a step
 * that did not improve", and undid it by stepping the other way - walking the gate off the
 * best slot it had just found. We ask the calculator's own gatePosition instead, which moves
 * synchronously on click; the drawing is then allowed to catch up in its own time.
 *
 * The set of positions is NOT FIXED. Moving the gate re-mixes the panels, so the run you
 * walk back through is not always the run you measured on the way out. An earlier draft
 * scanned the whole run, remembered the best index and walked back k steps to it; the same
 * request landed on 6885 one run and 3835 the next. Stepping forward while it improves, with
 * a single one-step correction at the end, never leans on that assumption.
 *
 * WHY WE DRIVE THE REAL MOVE BUTTONS. Every position this lands on is one the calculator
 * itself produced through its own handler, so nothing is ever built that the calculator
 * would not have built on its own. Driving calculator state directly is what has
 * manufactured false findings on this project repeatedly.
 *
 * IT NEVER DEAD-ENDS AND NEVER TAKES THE PAGE OVER BLIND. If the drawing cannot be read -
 * the summed dimensions must reconcile to the section length to the millimetre - it does
 * not touch the event at all and their original handler runs exactly as before.
 */
(function () {
  'use strict';

  if (window.__cfGateExactRefine) return;
  window.__cfGateExactRefine = true;

  var PATHS = [
    '/pages/perfmesh-fence', '/pages/blade-fence-calculator',
    '/pages/flat-top-fence-calculator', '/pages/base-mount-batten-calculator',
    '/pages/glass-fencing', '/pages/fence-calculator'
  ];
  var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  if (PATHS.indexOf(path) === -1) return;

  var MAX_STEPS = 24;          /* a run has far fewer slots than this - measured 4 on 8725mm */

  /* THE REDRAW IS NOT UNIFORMLY SYNCHRONOUS, and assuming it was cost a whole debugging pass.
     Measured on the same 8725mm raked section: moving slot 3 -> 2 updates the drawing in the
     same tick, but slot 1 -> 0 takes longer than 70ms. Moving into slot 0 puts the gate hard
     against the rake, which re-mixes the panels - a heavier re-solve than a plain shuffle.

     So do not guess a settle time. Whether the gate MOVED is answered instantly by the slot
     index; this cap only bounds how long we then wait for the drawing to catch up so it can
     be measured. A real move is picked up the moment it lands, and the full wait is never
     paid at the ends of the run - those return immediately. */
  var SETTLE_TICK = 50;
  var SETTLE_MAX = 2000;   /* only ever paid when the slot moved but the drawing lags */

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function sect(n) { var c = inst(); return c && c.state && c.state.sections && c.state.sections[n]; }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function sectionLength(n) {
    var s = sect(n);
    return (s && parseInt(s.length, 10)) || 0;
  }
  function gateWidth(n) {
    var s = sect(n);
    return (s && parseInt(s.gateLength, 10)) || 0;
  }

  /* THE DRAWING HAS TWO LABEL ROWS, and telling them apart is the whole job. Measured live
     on PerfMesh 8725mm raked, with a gate and a cut panel:

       y=224    50  1185  50  970  50  2500  50  3000  50  770  50    <- run dimensions
       y=236            3000      Gate       Cut                      <- annotations

     That second "3000" is the RAKE'S RETAINED HEIGHT, not a panel. Sorting every label by x
     - which is what the first cut of this file did - swallows it as an extra dimension and
     the run then "sums" to 11725 against a section of 8725.

     So: group by y and take the row that is entirely dimensions AND sums to the section
     length. That test SELECTS the dimension row rather than merely checking it, and it is
     self-proving - a row reconciling to the section length to the millimetre is the run. If
     no row does, or more than one does, we return null and stay out of the way entirely.

     TWO LABEL STYLES, both of which occur. The renderer writes the raw form and something
     later rewrites it to the short form, so the same fence reads either way depending on
     when you look:

       raw     "50mm Post"  "1185mm"  "2500mm"      annotations "Rake (ret. 3000mm)" "Cut"
       short   "50"         "1185"    "2500"        annotations "3000" "Cut"

     Measured both live within a few seconds of each other on the same section. Accepting
     only the short form - which an earlier draft did - made most readings come back null.
     The pattern below takes either. */
  function rowsOf() {
    var svg = document.querySelector('#section-preview svg');
    if (!svg) return null;
    var byY = {};
    [].forEach.call(svg.querySelectorAll('text'), function (t) {
      if (t.style.display === 'none') return;
      var x = parseFloat(t.getAttribute('x'));
      var y = parseFloat(t.getAttribute('y'));
      var txt = (t.textContent || '').replace(/\s+/g, ' ').trim();
      if (!isFinite(x) || !isFinite(y) || !txt) return;
      var k = Math.round(y);
      (byY[k] = byY[k] || []).push({ x: x, txt: txt });
    });
    return byY;
  }

  /* The run left to right, each entry carrying its own offset from the left end. */
  function dimRow(L) {
    var byY = rowsOf();
    if (!byY) return null;
    var re = /^(\d+)(?:\s*mm)?(?:\s+(?:Post|Panel|Gate|Cut|Raked?))?$/i;
    var found = null, hits = 0;

    Object.keys(byY).forEach(function (k) {
      var row = byY[k].slice().sort(function (a, b) { return a.x - b.x; });
      var vals = [], sum = 0, ok = true;
      for (var i = 0; i < row.length; i++) {
        var m = row[i].txt.match(re);
        if (!m) { ok = false; break; }
        var v = parseInt(m[1], 10);
        vals.push({ x: row[i].x, n: v, start: sum });
        sum += v;
      }
      if (ok && vals.length >= 3 && sum === L) { hits++; found = vals; }
    });
    return hits === 1 ? found : null;
  }

  /* Where the gate actually starts, in mm from the left end. The "Gate" annotation sits
     directly under its own dimension label, so match on x - then insist the panel it landed
     on really is the gate width. Two independent agreements before we trust the reading. */
  function measure(n) {
    var L = sectionLength(n), gw = gateWidth(n);
    if (!L || !gw) return null;

    var dim = dimRow(L);
    if (!dim) return null;

    var byY = rowsOf(), gx = null;
    Object.keys(byY).forEach(function (k) {
      byY[k].forEach(function (o) {
        if (gx === null && /^gate$/i.test(o.txt)) gx = o.x;
      });
    });
    if (gx === null) return null;

    var best = null, bd = 1e9;
    dim.forEach(function (d) {
      var dd = Math.abs(d.x - gx);
      if (dd < bd) { bd = dd; best = d; }
    });
    if (!best || bd > 2 || best.n !== gw) return null;

    return best.start;
  }

  /* The raked panel as a span in mm along the run, or null if we cannot identify it with
     confidence. A rake only ever sits at one END of a run - the state calls it "left-raked"
     or "right-raked" - so once we know which end, the drawing gives us its width: the first
     panel after the opening post, or the last one before the closing post. The drawing is
     used for the geometry because it is the thing that reconciles; state is consulted only
     for the two facts it states plainly, that a rake exists and which end it is on. */
  function rakeSpan(n, dim) {
    var s = sect(n);
    if (!s || !s.hasRaked || !dim || dim.length < 3) return null;

    var panels = s.renderedPanels;
    if (Object.prototype.toString.call(panels) !== '[object Array]') return null;

    var side = null;
    panels.forEach(function (p) {
      if (!p || !p.isRaked || typeof p.type !== 'string') return;
      if (/left/i.test(p.type)) side = 'left';
      else if (/right/i.test(p.type)) side = 'right';
    });
    if (!side) return null;

    var d = side === 'left' ? dim[1] : dim[dim.length - 2];
    if (!d) return null;
    return { start: d.start, end: d.start + d.n, side: side };
  }

  function moveBtn(n, dir) { return document.getElementById('section-' + n + '-gate-move-' + dir); }

  /* Returns 'moved', 'end' or 'unreadable'.

     'end' means the click did nothing because the gate is already at that end of the run.
     THE MOVE BUTTONS DO NOT DISABLE AT THE ENDS - measured live: at slot 0 the Move-left
     button reports disabled=false and clicking it leaves the gate exactly where it was. So
     the only reliable way to know we have run out of road is to look at whether the drawing
     actually changed. */
  /* A single unreadable reading is almost always transient: the renderer writes the raw
     labels and a later pass rewrites them to the short form, and a read landing mid-rewrite
     sees a row that does not reconcile. Retrying a few times costs nothing and removes the
     whole class of spurious failure - without it a scan aborted part way through, which left
     the gate moved with no note against it. */
  async function measureStable(n) {
    var m = measure(n);
    for (var i = 0; i < 10 && m === null; i++) {
      await sleep(40);
      m = measure(n);
    }
    return m;
  }

  /* The calculator's own slot index for the gate. */
  function gatePos(n) {
    var s = sect(n);
    return (s && typeof s.gatePosition === 'number') ? s.gatePosition : null;
  }

  async function step(n, dir) {
    var b = moveBtn(n, dir);
    if (!b || b.disabled) return 'end';

    var beforeSlot = gatePos(n);
    var before = await measureStable(n);
    b.click();

    /* ASK THE SLOT INDEX, NOT THE DRAWING, WHETHER ANYTHING HAPPENED. gatePosition updates
       SYNCHRONOUSLY on click (measured: immediately after clicking Move-left from slot 3 the
       state already reads 2), while the redraw lags by a variable amount. Judging "did it
       move" from the drawing alone meant a slow re-solve looked identical to the end of the
       run: the second placement on a page kept walking left to the rake and then reading its
       own Move-right as a no-op, so it stopped there and wrote nothing. Moving OUT of the
       slot against the rake is exactly the heavy re-solve that outruns a short wait. */
    if (beforeSlot !== null && gatePos(n) === beforeSlot) return 'end';

    var waited = 0, after;
    while (waited < SETTLE_MAX) {
      await sleep(SETTLE_TICK);
      waited += SETTLE_TICK;
      after = measure(n);
      if (after !== null && after !== before) return 'moved';
    }

    after = await measureStable(n);
    if (after === null) return 'unreadable';
    return 'moved';   /* the slot index says it moved - trust that over a slow redraw */
  }

  function note(n) { return document.getElementById('cf-gate-exact-note-' + n); }

  async function place(n) {
    var inp = document.getElementById('cf-gate-exact-input-' + n);
    var sideEl = document.getElementById('cf-gate-exact-side-' + n);
    var want = parseInt(inp && inp.value, 10);
    var el = note(n);

    var L = sectionLength(n), gw = gateWidth(n);
    if (!L || !gw) return;

    if (isNaN(want)) { if (el) el.textContent = 'Enter a distance in mm.'; return; }

    var fromRight = !!(sideEl && sideEl.value === 'right');
    var target = fromRight ? (L - want - gw) : want;

    /* Walk to the left end, then step right for as long as each move gets us CLOSER to what
       was asked. Offsets increase monotonically left to right, so the distance to the target
       falls and then rises: the first move that does not improve means we have just passed
       the best spot, and one step back lands on it.

       This replaces an earlier version that scanned the whole run, remembered the best index
       and then walked back k steps to it. That assumed the set of positions is fixed, and it
       is not - moving the gate re-mixes the panels, so the run you walk back through is not
       always the run you measured on the way out, and the same request could land on 6885
       one time and 3835 the next. One overshoot and one step back never relies on that.

       Whatever happens, the note below reports the position we actually MEASURE at the end,
       so the customer is never shown a number the gate is not at. */
    var guard = 0, r, m, cur, d, bestD, lastGood = null;

    while (guard++ < MAX_STEPS) {
      r = await step(n, 'left');
      if (r !== 'moved') break;
    }

    cur = await measureStable(n);
    if (cur === null) return;          /* nothing measured yet, so nothing has been claimed */
    lastGood = cur;
    bestD = Math.abs(cur - target);

    guard = 0;
    while (guard++ < MAX_STEPS) {
      r = await step(n, 'right');
      if (r !== 'moved') break;

      m = await measureStable(n);
      if (m === null) break;
      lastGood = m;

      d = Math.abs(m - target);
      if (d < bestD) { bestD = d; continue; }

      await step(n, 'left');           /* just passed the best spot - come back one */
      break;
    }

    if (!el) return;

    /* ONCE THE GATE HAS MOVED, SAY SOMETHING. An earlier draft returned silently when this
       last reading came back unreadable, which left the customer looking at a gate that had
       jumped with nothing to explain it - the worst of both worlds, and reproduced twice.
       Fall back to the last position we did read cleanly, and if there is no such reading,
       still speak plainly rather than saying nothing at all. */
    var end = await measureStable(n);
    if (end === null) end = lastGood;

    if (end === null) {
      el.textContent = 'Gate moved to the closest spot we can build to your ' + want
        + 'mm - have a look at the drawing above. Give us a call if it is not where you want it.';
      return;
    }

    /* Answer in the frame the customer asked in. Someone who typed "1200 from the right"
       should not have to convert 6885-from-the-left in their head to check we heard them.

       A gate hangs off posts, so it can only land on a panel join - measured on this section
       there are just four places it can go. An exact request often cannot be met to the
       millimetre, and saying so plainly is the honest answer. Per Pete's standing rule this
       is never framed as a failure and never dead-ends: it states what we set, what was
       asked for, why they differ, and that we will sort it out on the phone. */
    var right = L - end - gw;
    var got = fromRight ? right : end;
    var side = fromRight ? 'from the right' : 'from the left';
    var lead = 'Gate set ' + got + 'mm ' + side;

    /* Did they ask for the gate where the RAKE is? A raked panel is cut to suit the slope
       and cannot be cut again, so the gate cannot start part way along it. Pete: "we cant
       cut a rake panel but we could snap the gate up to it if the rake is in the way" -
       which is what the scan does anyway, since the join beside the rake is the nearest
       buildable spot. This just tells the customer that is what happened, and why. */
    var rake = rakeSpan(n, dimRow(L));
    var onRake = !!(rake && target < rake.end && (target + gw) > rake.start);

    el.textContent = Math.abs(got - want) <= 1
      ? lead + '.'
      : onRake
        ? lead + ' - right up against the raked panel. The raked panel is made to suit your '
          + 'slope and cannot be cut, so the gate sits beside it rather than part way along '
          + 'it. Give us a call if you need it further along and we will work the panel '
          + 'spacing around it.'
        : lead + ' - the closest we can build to your ' + want + 'mm. Gates hang off the '
          + 'posts, so they land on a panel join. Give us a call and we can work the spacing '
          + 'around the spot you want.';
  }

  /* CAPTURE phase on document, so we get the click before the button's own listener. When
     the drawing is readable we stop the event dead and do the job properly; when it is not,
     we do nothing and let cf-gate-controls.js behave exactly as it always has. Two scripts
     both driving the Move buttons is what made this nondeterministic before - the same
     request landed on 6885 one run and 3835 the next - so only one of us may act. */
  function claim(n) {
    return sectionLength(n) && gateWidth(n) && measure(n) !== null;
  }

  /* One placement at a time. A second Apply while the first is still walking the gate would
     put two scans on the same Move buttons, which is the very race we took this over to
     remove - and an impatient double-click is the most ordinary thing a customer does. The
     click is still swallowed so their handler cannot start one either. */
  var busy = false;
  var BUSY_MAX = 20000;

  async function run(n) {
    if (busy) return;
    busy = true;
    try {
      /* The flag must ALWAYS come back down. Seen during testing: one placement did not
         settle, busy stayed true, and every later Apply was swallowed - the button looked
         dead for the rest of the session, which is worse than the bug being fixed. Racing
         against a timeout guarantees the control recovers on its own. A full placement is a
         handful of clicks and finishes in a second or two, so 20s only ever fires on a
         genuine stall. */
      await Promise.race([place(n), sleep(BUSY_MAX)]);
    }
    catch (err) { if (window.console && console.debug) console.debug('[cf-gate-exact-refine]', err); }
    finally { busy = false; }
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    var b = (t && t.closest) ? t.closest('[id^="cf-gate-exact-apply-"]') : null;
    if (!b) return;
    var m = b.id.match(/^cf-gate-exact-apply-(\d+)$/);
    if (!m || !claim(m[1])) return;
    e.stopImmediatePropagation();
    run(m[1]);
  }, true);

  /* Enter in the distance field runs their exactApply too - same treatment. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var t = e.target;
    if (!t || !t.id) return;
    var m = String(t.id).match(/^cf-gate-exact-input-(\d+)$/);
    if (!m || !claim(m[1])) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    run(m[1]);
  }, true);

  try { console.log('[cf-gate-exact-refine] installed'); } catch (e) {}
})();
